import React from 'react';
import { createPageUrl } from "@/utils";
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Trash2, Pencil, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { getKindFlags, loadSourceKindFlags } from '@/services/sourceKinds';

const STATUS_CONFIG = {
  'Active':      { bg: 'rgba(77,179,0,0.15)',    color: 'rgba(77,179,0,0.90)'    },
  'Deprecated':  { bg: 'rgba(222,111,107,0.20)', color: 'rgba(222,111,107,0.90)' },
  'Maintenance': { bg: 'rgba(233,171,48,0.20)',  color: 'rgba(233,171,48,0.90)'  },
  // A source with no mappings yet — /api/sources computes this state.
  'Draft':       { bg: 'rgba(99,102,241,0.15)',  color: 'rgba(99,102,241,0.95)'  },
};

// Which kinds are agentic (created and verified by an AI master) comes from the
// source_kind registry via @/services/sourceKinds — family === 'agentic' on the
// catalog row. That service keeps the old hardcoded set as its fallback.
export function AgenticBadge() {
  return (
    <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
      style={{ background: 'rgba(139,92,246,0.15)', color: 'rgba(139,92,246,0.95)' }}>
      <Sparkles size={11} /> AI-built
    </span>
  );
}

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)' };
  return (
    <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2"
      style={{ backgroundColor: cfg.bg }}>
      <span className="size-[10px] rounded-full shrink-0" style={{ backgroundColor: cfg.color }} />
      <span className="text-xs font-medium whitespace-nowrap leading-none" style={{ color: cfg.color }}>{status}</span>
    </span>
  );
}

const COL = {
  name:    'flex-1 min-w-0',
  type:    'w-[120px] shrink-0',
  dir:     'w-[90px]  shrink-0',
  status:  'w-[120px] shrink-0',
  created: 'w-[120px] shrink-0',
  actions: 'w-[80px]  shrink-0',
};

const DIR_LABEL = { read: 'Read', write: 'Write', both: 'Read · Write' };
function DirPill({ direction }) {
  const label = DIR_LABEL[direction] || direction || '—';
  const color = direction === 'write' ? 'rgba(233,171,48,0.9)' : direction === 'both' ? 'rgba(99,102,241,0.95)' : 'rgba(55,122,214,0.95)';
  return (
    <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
      style={{ background: 'rgba(120,140,170,0.14)', color }}>{label}</span>
  );
}

export default function DataSourcesTable({ sources, isLoading, onEdit, onDelete, onOpenTarget }) {
  const navigate = useNavigate();
  // Re-render once the source_kind catalog resolves; until then getKindFlags
  // answers from its fallback set, so badges are correct on the first paint too.
  const [kindFlagsVersion, setKindFlagsVersion] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    loadSourceKindFlags().then(() => { if (alive) setKindFlagsVersion((v) => v + 1); });
    return () => { alive = false; };
  }, []);
  const isAgentic = React.useCallback(
    (kind) => getKindFlags(kind).is_agentic,
    [kindFlagsVersion], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="flex flex-col min-w-[820px]">
      {/* Header */}
      <div
        className="flex items-center h-9 px-5 gap-[10px] border border-table-header"
        style={{ background: 'var(--table-header-bg)', borderRadius: '8px 8px 0 0' }}
      >
        <div className={`${COL.name} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Source name</div>
        <div className={`${COL.type} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Type</div>
        <div className={`${COL.dir} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Direction</div>
        <div className={`${COL.status} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Status</div>
        <div className={`${COL.created} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Created</div>
        <div className={COL.actions} />
      </div>

      {/* Rows */}
      {isLoading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center h-11 px-5 gap-[10px]"
            style={{ background: i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)' }}>
            <Skeleton className="h-4 w-48" />
          </div>
        ))
      ) : sources.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm" style={{ color: 'var(--page-muted)' }}>No data sources found</p>
        </div>
      ) : (
        sources.map((source, i) => (
          <div
            key={source.id}
            className="group flex items-center h-11 px-5 gap-[10px] cursor-pointer"
            style={{ background: i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)' }}
            onClick={() => {
              // Routing by storage type:
              //  • FILE sources (csv/xlsx/parquet/json/file) → the rich file detail
              //    PAGE (DataSourceView: upload, analysis, mappings, processing).
              //  • EVERY OTHER type (postgres/mysql/sqlserver/oracle/snowflake/
              //    bigquery/redshift/mongo/s3), read OR write → the full
              //    StorageDetail PAGE (Connection / Analytics / Mappings / Schema /
              //    Records), type-generic via the node catalog.
              const eid = source.entity_id ?? source.id;
              const type = (source.kind || source.source_type || '').toLowerCase();
              const isFile = ['file', 'csv', 'csv_upload', 'xlsx', 'xls', 'parquet', 'json', 'ndjson'].includes(type);
              if (!isFile) { navigate(createPageUrl(`storages/${source.origin}/${eid}`)); return; }
              if (source.origin === 'data_target') { onOpenTarget?.(source); return; }
              navigate(createPageUrl(`data-sources/${eid}`));
            }}
          >
            <div className={`${COL.name} min-w-0`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate text-page-text group-hover:text-nav-active transition-colors">
                  {source.source_name}
                </span>
                {source.is_golden_source && (
                  <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
                    style={{ background: 'rgba(233,171,48,0.18)', color: 'rgba(180,120,0,0.85)' }}>
                    Golden
                  </span>
                )}
                {isAgentic(source.kind) && <AgenticBadge />}
              </div>
              {source.description && (
                <div className="text-xs truncate" style={{ color: 'var(--page-muted)', fontWeight: 400 }}>{source.description}</div>
              )}
            </div>
            <div className={`${COL.type} text-sm`} style={{ color: 'var(--page-text)', fontWeight: 400 }}>
              {source.kind || source.source_type || '—'}
            </div>
            <div className={COL.dir}>
              <DirPill direction={source.direction} />
            </div>
            <div className={COL.status}>
              <StatusPill status={source.status} />
            </div>
            <div className={COL.created}>
              <span className="text-sm" style={{ color: 'var(--page-text)', fontWeight: 400 }}>
                {(() => { const d = source.created_date ? new Date(source.created_date) : null; return d && !isNaN(d) ? format(d, 'MMM d, yyyy') : '—'; })()}
              </span>
            </div>
            <div className={`${COL.actions} flex justify-end gap-2`} onClick={(e) => e.stopPropagation()}>
              {source.origin !== 'data_target' && onEdit && (
                <button
                  onClick={() => onEdit(source)}
                  title="Edit"
                  className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors"
                >
                  <Pencil size={16} />
                </button>
              )}
              <button
                onClick={() => onDelete(source)}
                title="Delete"
                className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
